import React, { useEffect, useMemo, useState } from "react";
import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAdminAuth } from "../context/AdminAuthContext";

const parseUserId = (path) => {
  const segments = path.split("/");
  const userIndex = segments.indexOf("users");
  if (userIndex === -1) return "unknown";
  return segments[userIndex + 1] || "unknown";
};

const formatDate = (value) => {
  if (!value) return "N/A";
  if (typeof value === "number") return new Date(value).toLocaleString("en-GB");
  if (value.toDate) return value.toDate().toLocaleString("en-GB");
  return String(value);
};

const getSortValue = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (value.toDate) return value.toDate().getTime();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getStatusClass = (status) => {
  const value = String(status || "processing").toLowerCase();
  if (value === "approved") return "success";
  if (value === "rejected") return "danger";
  return "warning";
};

const Wallet = () => {
  const { user: currentUser, role: currentRole } = useAdminAuth();
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [historyWithdrawals, setHistoryWithdrawals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [userProfiles, setUserProfiles] = useState({});
  const [loadingPending, setLoadingPending] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    const loadWithdrawals = async () => {
      setLoadingPending(true);
      setLoadingHistory(true);
      setSelected(null);
      try {
        const base = collectionGroup(db, "withdrawals");
        const [pendingSnap, approvedSnap, rejectedSnap] = await Promise.all([
          getDocs(
            query(
              base,
              where("status", "==", "processing"),
              orderBy("requestedAt", "desc"),
              limit(100)
            )
          ),
          getDocs(
            query(
              base,
              where("status", "==", "approved"),
              orderBy("requestedAt", "desc"),
              limit(100)
            )
          ),
          getDocs(
            query(
              base,
              where("status", "==", "rejected"),
              orderBy("requestedAt", "desc"),
              limit(100)
            )
          ),
        ]);

        const pendingRows = pendingSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          userId: parseUserId(docSnap.ref.path),
          ...docSnap.data(),
        }));
        const approvedRows = approvedSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          userId: parseUserId(docSnap.ref.path),
          ...docSnap.data(),
        }));
        const rejectedRows = rejectedSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          userId: parseUserId(docSnap.ref.path),
          ...docSnap.data(),
        }));
        const historyRows = [...approvedRows, ...rejectedRows].sort((a, b) => {
          const aValue = getSortValue(a.requestedAt || a.updatedAt);
          const bValue = getSortValue(b.requestedAt || b.updatedAt);
          return bValue - aValue;
        });
        setPendingWithdrawals(pendingRows);
        setHistoryWithdrawals(historyRows);
      } catch (error) {
        console.error("Failed to load withdrawals", error);
      } finally {
        setLoadingPending(false);
        setLoadingHistory(false);
      }
    };

    loadWithdrawals();
  }, []);

  useEffect(() => {
    const ids = new Set();
    pendingWithdrawals.forEach((item) => {
      if (item.userId) ids.add(item.userId);
    });
    historyWithdrawals.forEach((item) => {
      if (item.userId) ids.add(item.userId);
    });
    if (selected?.userId) ids.add(selected.userId);

    const missing = Array.from(ids).filter((id) => !(id in userProfiles));
    if (!missing.length) return () => {};

    let isMounted = true;
    const loadProfiles = async () => {
      try {
        const docs = await Promise.all(
          missing.map((uid) => getDoc(doc(db, "users", uid)))
        );
        if (!isMounted) return;
        const next = docs.reduce((acc, snap, index) => {
          acc[missing[index]] = snap.exists() ? snap.data() : null;
          return acc;
        }, {});
        if (Object.keys(next).length) {
          setUserProfiles((prev) => ({ ...prev, ...next }));
        }
      } catch (error) {
        console.error("Failed to load user profiles", error);
      }
    };

    loadProfiles();

    return () => {
      isMounted = false;
    };
  }, [db, historyWithdrawals, pendingWithdrawals, selected?.userId, userProfiles]);

  const getUserLabel = (userId) => {
    const profile = userId ? userProfiles[userId] : null;
    return (
      profile?.username ||
      profile?.name ||
      profile?.displayName ||
      userId ||
      "User"
    );
  };

  const handleStatusUpdate = async (status) => {
    if (!selected) return;
    if (selected.status && selected.status !== "processing") return;
    try {
      const ref = doc(
        db,
        "users",
        selected.userId,
        "withdrawals",
        selected.id
      );
      await updateDoc(ref, {
        status,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid || null,
        updatedByRole: currentRole || null,
      });

      const updatedItem = {
        ...selected,
        status,
        updatedAt: new Date(),
      };
      setPendingWithdrawals((prev) =>
        prev.filter((item) => item.id !== selected.id)
      );
      setHistoryWithdrawals((prev) => {
        const next = [
          updatedItem,
          ...prev.filter((item) => item.id !== selected.id),
        ];
        next.sort((a, b) => {
          const aValue = getSortValue(a.requestedAt || a.updatedAt);
          const bValue = getSortValue(b.requestedAt || b.updatedAt);
          return bValue - aValue;
        });
        return next;
      });
      setSelected(updatedItem);
    } catch (error) {
      console.error("Failed to update withdrawal", error);
    }
  };

  const summary = useMemo(() => {
    const pendingAmount = pendingWithdrawals.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0
    );
    const approvedItems = historyWithdrawals.filter(
      (item) => String(item.status).toLowerCase() === "approved"
    );
    const rejectedItems = historyWithdrawals.filter(
      (item) => String(item.status).toLowerCase() === "rejected"
    );
    const approvedAmount = approvedItems.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0
    );
    const rejectedAmount = rejectedItems.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0
    );
    return {
      pendingCount: pendingWithdrawals.length,
      pendingAmount,
      approvedCount: approvedItems.length,
      approvedAmount,
      rejectedCount: rejectedItems.length,
      rejectedAmount,
    };
  }, [historyWithdrawals, pendingWithdrawals]);

  const selectedStatus = selected?.status || "processing";

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Wallet & Payments</h1>
          <p>Approve payout requests and track wallet activity.</p>
        </div>
      </div>

      <div className="stats-inline">
        <div className="stat-mini">
          <div className="stat-label">Pending</div>
          <div className="stat-value">{summary.pendingCount}</div>
          <div className="stat-meta">₹{summary.pendingAmount.toFixed(2)}</div>
        </div>
        <div className="stat-mini">
          <div className="stat-label">Approved</div>
          <div className="stat-value">{summary.approvedCount}</div>
          <div className="stat-meta">₹{summary.approvedAmount.toFixed(2)}</div>
        </div>
        <div className="stat-mini">
          <div className="stat-label">Rejected</div>
          <div className="stat-value">{summary.rejectedCount}</div>
          <div className="stat-meta">₹{summary.rejectedAmount.toFixed(2)}</div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card table-card">
          <div className="card-header">
            <h3>Pending approvals</h3>
            <span className="chip">{pendingWithdrawals.length} items</span>
          </div>
          {loadingPending ? (
            <div className="empty-inline">Loading pending approvals...</div>
          ) : pendingWithdrawals.length === 0 ? (
            <div className="empty-inline">No pending withdrawals.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Requested</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingWithdrawals.map((item) => (
                    <tr
                      key={`${item.userId}-${item.id}`}
                      className={selected?.id === item.id ? "active-row" : ""}
                      onClick={() => setSelected(item)}
                    >
                      <td>
                        <div className="table-title" title={item.userId || ""}>
                          {getUserLabel(item.userId)}
                        </div>
                        <div className="table-subtitle">{item.id}</div>
                      </td>
                      <td>₹{Number(item.amount || 0).toFixed(2)}</td>
                      <td>
                        <span
                          className={`status-pill ${getStatusClass(item.status)}`}
                        >
                          {item.status || "processing"}
                        </span>
                      </td>
                      <td>{formatDate(item.requestedAt || item.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card detail-card">
          <div className="card-header">
            <h3>Request details</h3>
          </div>
          {!selected ? (
            <div className="empty-inline">Select a withdrawal request.</div>
          ) : (
            <div className="detail-body">
              <div className="detail-row">
                <div className="detail-label">User</div>
                <div className="detail-value" title={selected.userId || ""}>
                  {getUserLabel(selected.userId)}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Amount</div>
                <div className="detail-value">
                  ₹{Number(selected.amount || 0).toFixed(2)}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Status</div>
                <div className="detail-value">{selectedStatus}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Requested</div>
                <div className="detail-value">
                  {formatDate(selected.requestedAt || selected.updatedAt)}
                </div>
              </div>
              {selected.updatedAt ? (
                <div className="detail-row">
                  <div className="detail-label">Updated</div>
                  <div className="detail-value">
                    {formatDate(selected.updatedAt)}
                  </div>
                </div>
              ) : null}

              <div className="divider" />

              {selectedStatus === "processing" ? (
                <>
                  <div className="inline-actions">
                    <button
                      className="button primary"
                      type="button"
                      onClick={() => handleStatusUpdate("approved")}
                    >
                      Approve
                    </button>
                    <button
                      className="button danger"
                      type="button"
                      onClick={() => handleStatusUpdate("rejected")}
                    >
                      Reject
                    </button>
                  </div>
                  <div className="helper-text">
                    Rejecting a withdrawal triggers an automated refund to the
                    user wallet.
                  </div>
                </>
              ) : (
                <div className="empty-inline">
                  This request is already {selectedStatus}.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card table-card">
        <div className="card-header">
          <h3>Decisioned withdrawals</h3>
          <span className="chip">{historyWithdrawals.length} items</span>
        </div>
        {loadingHistory ? (
          <div className="empty-inline">Loading approval history...</div>
        ) : historyWithdrawals.length === 0 ? (
          <div className="empty-inline">No approved or rejected requests.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Requested</th>
                </tr>
              </thead>
              <tbody>
                {historyWithdrawals.map((item) => (
                  <tr
                    key={`${item.userId}-${item.id}`}
                    className={selected?.id === item.id ? "active-row" : ""}
                    onClick={() => setSelected(item)}
                  >
                    <td>
                      <div className="table-title" title={item.userId || ""}>
                        {getUserLabel(item.userId)}
                      </div>
                      <div className="table-subtitle">{item.id}</div>
                    </td>
                    <td>₹{Number(item.amount || 0).toFixed(2)}</td>
                    <td>
                      <span
                        className={`status-pill ${getStatusClass(
                          item.status
                        )}`}
                      >
                        {item.status || "processing"}
                      </span>
                    </td>
                    <td>{formatDate(item.requestedAt || item.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wallet;
